import { put, select, all, takeEvery, call, cancelled, takeLatest } from 'redux-saga/effects';
import { setCookie, getCookie, serializeAEMCookieCollection, deserializeAEMCookieCollection } from 'utils';
import { getDomainSelector, getIsAuthorized, getRegionCode } from 'common/selectors';
import { api } from 'api';
import log from 'loglevel';
import { EffectR } from 'types/utils';
import { getActiveTintColor, getSelectedTintColors } from 'features/product-info';
import { getBaseEntityIdForTintService } from 'features/related-services';
import { TTintColorWithQuantity } from 'features/product-info/components/pdp-select-tint-color';
import { getIsTintServiceAvailableInRegion } from 'features/page-model';
import { BasketItem } from '../types';
import { getProductQuantity } from '../utils/get-product-quantity';
import { getBasket, getCartItem, getProductQuantityAccordingToSelectedServices } from './selectors';
import {
  BASKET_SET,
  BASKET_UPDATE_ITEM,
  BASKET_RESET,
  BasketFlowActions,
  UPDATE_TINT_SERVICE_IN_SERVICE_BASKET,
} from './actions';
import { setBasket } from './api';
import { handleUpdateTintServiceInServiceBasket } from './handle-update-tint-service-in-service-basket';

const COOKIE_KEY = 'cartInfo';

const serializeBasket = serializeAEMCookieCollection((item: BasketItem) => `${item.code}:${item.quantity}`);
const deserializeBasket = deserializeAEMCookieCollection<BasketItem>((item: string) => {
  const [code, qty] = item.split(':');
  return {
    code,
    quantity: parseFloat(qty),
  };
});

export function* resetBasket() {
  const basketFromCookies: BasketItem[] = yield deserializeBasket(getCookie(COOKIE_KEY) || '');
  yield put(BASKET_SET(basketFromCookies));
}

export function* updateBasketItem() {
  const basketArr: BasketItem[] = yield select(getBasket);
  const domain = (yield select(getDomainSelector)) as EffectR.Select<typeof getDomainSelector>;
  setCookie(COOKIE_KEY, serializeBasket(basketArr), {
    path: '/',
    domain,
    expires: new Date(new Date().getFullYear() + 1, 12, 31),
  });
  document.body.dispatchEvent(
    new CustomEvent('basket:update', {
      cancelable: true,
      bubbles: true,
      composed: true,
      detail: basketArr,
    }),
  );
  /**
   * API call should be last because of current basket web component implementation;
   * `change` handler fires twice & dispatches 2 basket flow actions for basket update
   */
  const isAuthorized = (yield select(getIsAuthorized)) as ReturnType<typeof getIsAuthorized>;
  const regionCode = (yield select(getRegionCode)) as ReturnType<typeof getRegionCode>;
  const abortController = (yield call([api, 'abortController'])) as ReturnType<
    (typeof api)['abortController']
  >;
  try {
    if (isAuthorized) {
      yield call(
        setBasket,
        {
          products: basketArr.map(({ code, quantity }) => ({ id: code, quantity })),
          regionCode: regionCode || undefined,
        },
        abortController.token,
      );
    }
  } catch (error) {
    yield call([log, 'error'], error);
  } finally {
    if ((yield cancelled()) as boolean) {
      abortController.cancel();
    }
  }
}

export function* rootSaga() {
  yield all([
    takeEvery(BASKET_RESET, () => resetBasket()),
    // takeLatest because we should cancel previous API call
    takeLatest(BASKET_UPDATE_ITEM, () => updateBasketItem()),
    takeEvery(BasketFlowActions.UPDATE_ITEM, handleBasketItemFlow),
    takeEvery(BasketFlowActions.RESET_ITEM, handleBasketResetItemFlow),
    takeEvery(BasketFlowActions.SYNC_ITEM_WITH_COOKIE, handleBasketSyncItemWithCookieFlow),
    takeEvery(UPDATE_TINT_SERVICE_IN_SERVICE_BASKET, handleUpdateTintServiceInServiceBasket),
  ]);
}

export function* handleBasketItemFlow({ payload }: { payload: BasketFlowActions.UpdateItemPayload }) {
  const { productId, flowQty } = payload;
  const item: BasketItem | undefined = (yield call(selectCartItem, productId)) as EffectR.Call<
    typeof selectCartItem
  >;
  const currentQty = item?.quantity ?? 0;
  const quantity = Number((currentQty + flowQty).toFixed(2));

  yield put(BASKET_RESET());
  yield put(BASKET_UPDATE_ITEM({ code: productId, quantity }));

  const isTintServiceAvailableInRegion: boolean = yield select(getIsTintServiceAvailableInRegion);

  if (isTintServiceAvailableInRegion) {
    const activeTintColor: TTintColorWithQuantity | null = yield select(getActiveTintColor);
    const isUserChangesTintServiceForProduct = activeTintColor !== null;

    const serviceIdForTint: string | null = yield select(getBaseEntityIdForTintService);
    const isServiceCanBeAddedToProduct = serviceIdForTint !== null;

    if (isUserChangesTintServiceForProduct && isServiceCanBeAddedToProduct) {
      const { HEX, COLOR_NAME, serviceQuantity } = activeTintColor;

      yield put(
        UPDATE_TINT_SERVICE_IN_SERVICE_BASKET({
          parentLineId: productId,
          flowQty,
          hex: HEX,
          color: COLOR_NAME,
          serviceIdForTint,
          currentServiceQuantity: serviceQuantity,
        }),
      );
    }
  }
}

export function* handleBasketResetItemFlow({ payload }: { payload: BasketFlowActions.ResetItemPayload }) {
  const { productId, context } = payload;
  const item = (yield call(selectCartItem, productId)) as EffectR.Call<typeof selectCartItem>;
  const currentQty = item?.quantity ?? 0;

  const isTintServiceAvailableInRegion: boolean = yield select(getIsTintServiceAvailableInRegion);

  const selectedTintColors: TTintColorWithQuantity[] | never[] = yield select(getSelectedTintColors);

  let flowQty;

  if (isTintServiceAvailableInRegion && selectedTintColors.length > 0) {
    const quantityOfSelectedColor: number = yield select(
      getProductQuantityAccordingToSelectedServices(productId),
    );

    flowQty = -quantityOfSelectedColor;
  } else {
    flowQty = -currentQty;
  }

  yield put(BasketFlowActions.UPDATE_ITEM({ productId, flowQty, context }));
}

export function* handleBasketSyncItemWithCookieFlow({
  payload,
}: {
  payload: BasketFlowActions.SyncItemWithCookiePayload;
}) {
  const { productId } = payload;
  const item = (yield call(selectCartItem, productId)) as EffectR.Call<typeof selectCartItem>;
  const currentQty = item?.quantity ?? 0;
  const quantityInBasketCookie = (yield call(
    selectProductQuantityInBasketCookie,
    productId,
  )) as EffectR.Call<typeof selectProductQuantityInBasketCookie>;
  const flowQty = quantityInBasketCookie - currentQty;
  if (flowQty !== 0) {
    yield put(
      BasketFlowActions.UPDATE_ITEM({
        productId,
        flowQty,
        ...(typeof payload?.isNeedGA === 'boolean' && { isNeedGA: payload.isNeedGA }),
      }),
    );
  }
}

export function* selectCartItem(productId: string) {
  return (yield select(getCartItem(productId))) as EffectR.Select<ReturnType<typeof getCartItem>>;
}

export function* selectProductQuantityInBasketCookie(productId: string) {
  const basketFromCookies: BasketItem[] = yield deserializeBasket(getCookie(COOKIE_KEY) || '');
  return (yield getProductQuantity(basketFromCookies, productId)) as EffectR.Select<
    ReturnType<typeof getProductQuantity>
  >;
}
